import React, { useState } from 'react';
import './ChangePasswordPopup.css';

interface ChangePasswordPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onChangePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const ChangePasswordPopup: React.FC<ChangePasswordPopupProps> = ({
  isOpen,
  onClose,
  onChangePassword,
  onShowToast,
}) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!oldPassword.trim()) {
      setError('Vui lòng nhập mật khẩu cũ');
      return;
    }

    if (!newPassword.trim()) {
      setError('Vui lòng nhập mật khẩu mới');
      return;
    }

    if (newPassword.length < 6) {
      setError('Mật khẩu mới phải ít nhất 6 ký tự');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Mật khẩu xác nhận không khớp');
      return;
    }

    if (oldPassword === newPassword) {
      setError('Mật khẩu mới phải khác mật khẩu cũ');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onChangePassword(oldPassword, newPassword);
      onShowToast('Đổi mật khẩu thành công!', 'success');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onClose();
    } catch (err) {
      console.error('[ChangePasswordPopup] Error changing password:', err);
      const msg = (err as any)?.message || 'Có lỗi xảy ra khi đổi mật khẩu';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="change-password-overlay" onClick={handleClose}>
      <div className="change-password-popup" onClick={(e) => e.stopPropagation()}>
        <div className="change-password-header">
          <h2 className="change-password-title">Đổi mật khẩu</h2>
          <button className="change-password-close-btn" onClick={handleClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="change-password-form">
          <div className="form-group">
            <label htmlFor="old-password">Mật khẩu cũ</label>
            <input
              type="password"
              id="old-password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              placeholder="Nhập mật khẩu cũ"
              required
              disabled={loading}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="new-password">Mật khẩu mới</label>
            <input
              type="password"
              id="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Nhập mật khẩu mới (ít nhất 6 ký tự)"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">Xác nhận mật khẩu mới</label>
            <input
              type="password"
              id="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Xác nhận mật khẩu mới"
              required
              disabled={loading}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="change-password-submit-btn" disabled={loading}>
            {loading ? 'Đang xử lý...' : 'Đổi mật khẩu'}
          </button>
        </form>

        <p className="change-password-info">
          Mật khẩu mới phải khác mật khẩu cũ và có ít nhất 6 ký tự.
        </p>
      </div>
    </div>
  );
};

export default ChangePasswordPopup;

